import { Box, CircularProgress } from '@mui/material';
import UPFileInput from 'components/file-input';
import { UPInputLabel } from 'components/input/styles';
import { CharacterCounter, FieldHint, FieldRow, MotivationTextArea } from 'modules/job-application/styles';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CONTRACT_COMPLIANCE__ATTACH_DOCUMENT,
  CONTRACT_COMPLIANCE__DOCUMENT_HINT,
  CONTRACT_COMPLIANCE__DOCUMENT_REJECTED,
  CONTRACT_COMPLIANCE__JUSTIFICATION,
  CONTRACT_COMPLIANCE__JUSTIFICATION_PLACEHOLDER,
  CONTRACT_COMPLIANCE__SUPPORTING_DOCUMENT,
} from 'translations/constants';
import { DOCUMENT_ACCEPT_ATTRIBUTE, JUSTIFICATION_MAX_LENGTH } from './constants';
import { UseJustificationResult } from './hooks/useJustification';
import { isAllowedJustificationDocument } from './utils';

interface JustificationFieldsProps {
  justification: UseJustificationResult;
  disabled?: boolean;
  testIdPrefix: string;
}

/**
 * Optional text (max 500 characters) and one supporting document. Same text area and file input as the
 * applicant form (motivation and CV fields).
 */
const JustificationFields = ({ justification, disabled, testIdPrefix }: JustificationFieldsProps): JSX.Element => {
  const [t] = useTranslation();
  const { text, setText, document, isUploading, uploadErrorMessage } = justification;
  // Shown in the field while the picked file uploads; the uploaded document's name replaces it.
  const [pendingFileName, setPendingFileName] = useState('');

  /** UPFileInput sends a synthetic `{ target: { files: [] } }` on clear, so both paths land here. */
  const onFileChange = (event: { target: { files?: FileList | File[] | null } }) => {
    const selected = event.target.files?.[0] ?? null;
    if (!selected) {
      setPendingFileName('');
      justification.clearDocument();
      return;
    }
    if (!isAllowedJustificationDocument(selected)) {
      setPendingFileName('');
      justification.clearDocument();
      justification.setUploadErrorMessage(t(CONTRACT_COMPLIANCE__DOCUMENT_REJECTED));
      return;
    }
    setPendingFileName(selected.name);
    justification.upload(selected);
  };

  const fileName = isUploading ? pendingFileName : document?.fileName ?? '';

  return (
    <>
      <Box>
        <UPInputLabel>{t(CONTRACT_COMPLIANCE__JUSTIFICATION)}</UPInputLabel>
        <MotivationTextArea
          value={text}
          onChange={event => setText(event.target.value)}
          aria-label={t(CONTRACT_COMPLIANCE__JUSTIFICATION)}
          placeholder={t(CONTRACT_COMPLIANCE__JUSTIFICATION_PLACEHOLDER)}
          minRows={3}
          maxLength={JUSTIFICATION_MAX_LENGTH}
          disabled={disabled}
          data-testid={`${testIdPrefix}-justification-text`}
        />
        <CharacterCounter
          isAtLimit={text.length >= JUSTIFICATION_MAX_LENGTH}
          data-testid={`${testIdPrefix}-justification-counter`}
        >
          {`${text.length}/${JUSTIFICATION_MAX_LENGTH}`}
        </CharacterCounter>
      </Box>

      <FieldRow>
        <UPFileInput
          value={fileName}
          onChange={onFileChange}
          label={t(CONTRACT_COMPLIANCE__SUPPORTING_DOCUMENT)}
          inputName="justificationDocument"
          placeholder={t(CONTRACT_COMPLIANCE__ATTACH_DOCUMENT)}
          accept={DOCUMENT_ACCEPT_ATTRIBUTE}
          // A justification document is evidence: upload it as picked, not shrunk.
          resizeImages={false}
          clearable
          disabled={disabled || isUploading}
          error={!!uploadErrorMessage}
          helperText={uploadErrorMessage ?? ''}
          testId={`${testIdPrefix}-justification-document`}
        />
        <FieldHint>{t(CONTRACT_COMPLIANCE__DOCUMENT_HINT)}</FieldHint>
        {isUploading && <CircularProgress size={16} data-testid={`${testIdPrefix}-justification-uploading`} />}
      </FieldRow>
    </>
  );
};

JustificationFields.defaultProps = {
  disabled: false,
};

export default JustificationFields;
